import axios, { AxiosResponse } from 'axios';
import { getApiUrl } from '../utils/runtimeConfig';
import { getIdToken } from './cognitoAuth';

interface ChatApiMessage {
  id: string;
  content: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  isLoading?: boolean;
}
type MessageCallback = (message: ChatApiMessage) => void;
type ChunkCallback = (chunk: string, isComplete: boolean) => void;
type ReasoningCallback = (reasoning: string, isStreaming: boolean) => void;
type ErrorCallback = (error: string) => void;
type ConnectionCallback = (connected: boolean) => void;

interface User {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  is_active: boolean;
  created_at: string;
}

// Base API configuration for chat with runtime detection
const CHAT_API_BASE_URL = getApiUrl();

class ChatAPIService {
  private static instance: ChatAPIService;
  private apiClient = axios.create({
    baseURL: CHAT_API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  private accessToken: string | null = null;
  private tokenExpiry: Date | null = null;
  private currentUser: User | null = null;

  // Session properties
  private currentSessionId: string | null = null;
  private isConnecting: boolean = false;

  // Callback handlers
  private messageCallbacks: Set<MessageCallback> = new Set();
  private chunkCallbacks: Set<ChunkCallback> = new Set();
  private reasoningCallbacks: Set<ReasoningCallback> = new Set();
  private errorCallbacks: Set<ErrorCallback> = new Set();
  private connectionCallbacks: Set<ConnectionCallback> = new Set();

  private constructor() {
    this.setupInterceptors();
    this.loadStoredAuth();
  }

  public static getInstance(): ChatAPIService {
    if (!ChatAPIService.instance) {
      ChatAPIService.instance = new ChatAPIService();
    }
    return ChatAPIService.instance;
  }

  private setupInterceptors(): void {
    // Request interceptor for authentication and logging
    this.apiClient.interceptors.request.use(
      async (config) => {
        console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);

        // Add Cognito ID token to Authorization header
        try {
          const idToken = await getIdToken();
          if (idToken) {
            config.headers.Authorization = `Bearer ${idToken}`;
          }
        } catch (error) {
          console.error('[ChatAPI] Failed to get ID token:', error);
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.apiClient.interceptors.response.use(
      (response) => {
        console.log(`Chat API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      async (error) => {
        console.error('Chat API Response Error:', error);
        
        if (error.response?.status === 404) {
          throw new Error('Chat service not found. Please ensure the backend is running.');
        } else if (error.response?.status === 500) {
          throw new Error('Chat service error occurred. Please try again.');
        } else if (error.code === 'ECONNREFUSED') {
          throw new Error('Unable to connect to chat service. Please ensure the backend is running.');
        }
        
        return Promise.reject(error);
      }
    );
  }

  private loadStoredAuth(): void {
    try {
      const storedToken = localStorage.getItem('chat_access_token');
      const storedExpiry = localStorage.getItem('chat_token_expiry');
      const storedUser = localStorage.getItem('chat_current_user');
      
      if (storedToken && storedExpiry && storedUser) {
        this.accessToken = storedToken;
        this.tokenExpiry = new Date(storedExpiry);
        this.currentUser = JSON.parse(storedUser);
        
        // Clear if expired
        if (!this.isTokenValid()) {
          this.clearAuth();
        }
      }
    } catch (error) {
      console.error('Error loading stored auth:', error);
      this.clearAuth();
    }
  }

  private storeAuth(token: string, expiresIn: number, user: User): void {
    this.accessToken = token;
    this.tokenExpiry = new Date(Date.now() + expiresIn * 1000);
    this.currentUser = user;
    
    localStorage.setItem('chat_access_token', token);
    localStorage.setItem('chat_token_expiry', this.tokenExpiry.toISOString());
    localStorage.setItem('chat_current_user', JSON.stringify(user));
  }

  private clearAuth(): void {
    this.accessToken = null;
    this.tokenExpiry = null;
    this.currentUser = null;
    
    localStorage.removeItem('chat_access_token');
    localStorage.removeItem('chat_token_expiry');
    localStorage.removeItem('chat_current_user');
  }

  private isTokenValid(): boolean {
    return this.tokenExpiry ? new Date() < this.tokenExpiry : false;
  }

  // Temporary authentication method - will be replaced with SSO
  public async authenticateTemporary(): Promise<User> {
    try {
      // Backend doesn't require authentication, return a mock user
      const mockUser: User = {
        id: 'demo-user-id',
        username: 'demo_user',
        email: 'demo@talrev.com',
        full_name: 'Demo User',
        is_active: true,
        created_at: new Date().toISOString()
      };

      // Store mock authentication for consistency
      this.currentUser = mockUser;
      console.log('Using demo user (no authentication required):', mockUser.username);
      return mockUser;
    } catch (error: any) {
      console.error('Demo user setup failed:', error);
      throw new Error(`Failed to setup demo user: ${error.message}`);
    }
  }

  // Create a new chat session
  public async createChatSession(): Promise<string> {
    try {
      // Ensure we're authenticated
      if (!this.isAuthenticated()) {
        console.log('Not authenticated, attempting temporary authentication...');
        await this.authenticateTemporary();
        console.log('Authentication completed, token:', this.accessToken ? 'present' : 'missing');
      }

      console.log('Creating new chat session...');
      const response: AxiosResponse<{session_id: string}> = await this.apiClient.post('/api/chat/session');
      console.log('Chat session created:', response.data.session_id);
      
      return response.data.session_id;
    } catch (error: any) {
      console.error('Failed to create chat session:', error);
      throw new Error(error.message || 'Failed to create chat session');
    }
  }

  // Send chat message
  public async sendMessage(message: string, sessionId: string): Promise<ChatApiMessage> {
    try {
      // Ensure we're authenticated
      if (!this.isAuthenticated()) {
        console.log('Not authenticated, attempting temporary authentication...');
        await this.authenticateTemporary();
        console.log('Authentication completed, token:', this.accessToken ? 'present' : 'missing');
      } else {
        console.log('Already authenticated, proceeding with message send');
      }

      const request = {
        content: message.trim()
      };

      console.log('Sending chat request to session:', sessionId, request);
      console.log('Using token:', this.accessToken ? 'Bearer ' + this.accessToken.substring(0, 20) + '...' : 'none');
      
      const response: AxiosResponse<any> = await this.apiClient.post(`/api/chat/${sessionId}/message`, request);
      console.log('Received chat response:', response.data);

      // Transform backend response to frontend format
      return {
        id: response.data.id || Date.now().toString(),
        content: response.data.content || response.data.response,
        sender: 'assistant',
        timestamp: new Date(response.data.timestamp || Date.now())
      };
    } catch (error: any) {
      console.error('Failed to send chat message:', error);
      throw new Error(error.message || 'Failed to send message');
    }
  }

  // Get current user
  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  // Check if authenticated
  public isAuthenticated(): boolean {
    // No authentication required for this API
    return true;
  }

  // Logout
  public logout(): void {
    this.clearAuth();
  }

  // Health check
  public async healthCheck(): Promise<boolean> {
    try {
      const response = await this.apiClient.get('/api/health');
      return response.status === 200;
    } catch (error) {
      console.warn('Chat API health check failed:', error);
      return false;
    }
  }

  // SSE streaming methods
  public async setupStreamingChat(sessionId?: string): Promise<string> {
    try {
      if (!this.isAuthenticated()) {
        await this.authenticateTemporary();
      }

      // Create a new session if none provided
      const finalSessionId = sessionId || await this.createChatSession();
      this.currentSessionId = finalSessionId;
      
      return finalSessionId;
    } catch (error: any) {
      console.error('Failed to setup streaming chat:', error);
      throw new Error(error.message || 'Failed to setup streaming chat');
    }
  }

  // Polling-based connection (no SSE needed)
  public async connectChatStream(sessionId: string): Promise<void> {
    console.log('Chat connection ready for polling-based messaging');
    this.currentSessionId = sessionId;
    this.isConnecting = false;
    this.notifyConnectionCallbacks(true);
  }

  public async connectProcessingStream(sessionId: string): Promise<void> {
    // No-op for polling-based system
    console.log('Processing stream not needed for polling');
  }

  public async sendStreamingMessage(message: string): Promise<void> {
    if (!this.currentSessionId) {
      throw new Error('No active chat session. Please connect first.');
    }

    // Send message and start polling for updates
    await this.sendMessageWithPolling(message, this.currentSessionId);
  }

  private async sendMessageWithPolling(message: string, sessionId: string): Promise<void> {
    try {
      const request = {
        content: message.trim()
      };

      console.log('Sending chat message with polling:', sessionId, request);

      // Send message and get message_id
      const response: AxiosResponse<any> = await this.apiClient.post(`/api/chat/${sessionId}/message`, request);
      const messageId = response.data.message_id;

      console.log('Message sent, starting polling for message_id:', messageId);

      // Start polling for status updates (don't await - let it run in background)
      this.pollMessageStatus(messageId, sessionId).catch(error => {
        console.error('Polling error:', error);
        this.notifyErrorCallbacks(error.message || 'Polling failed');
      });

    } catch (error: any) {
      console.error('Failed to send message with polling:', error);
      this.notifyErrorCallbacks(error.message || 'Failed to send message');
      throw error;
    }
  }

  private async pollMessageStatus(messageId: string, sessionId: string): Promise<void> {
    const pollInterval = 1000; // Poll every 1000ms (1 second)
    const maxAttempts = 60; // 60 seconds total
    let attempt = 0;
    let lastReasoningLength = 0;
    let lastPartialResponse = '';

    while (attempt < maxAttempts) {
      try {
        const response: AxiosResponse<any> = await this.apiClient.get(
          `/api/chat/${sessionId}/messages/${messageId}/status`
        );

        const data = response.data;
        console.log(`Poll attempt ${attempt + 1}:`, data.status);

        // Handle reasoning blocks
        if (data.reasoning_blocks && data.reasoning_blocks.length > lastReasoningLength) {
          // New reasoning blocks available
          const newBlocks = data.reasoning_blocks.slice(lastReasoningLength);
          for (const block of newBlocks) {
            const content = block.result || block.description || '';
            this.notifyReasoningCallbacks(content + '\n', true);
          }
          lastReasoningLength = data.reasoning_blocks.length;
        }

        // Handle partial response
        if (data.status === 'processing' && data.partial_response) {
          if (data.partial_response !== lastPartialResponse) {
            // Send the new content as a chunk
            const newContent = data.partial_response.substring(lastPartialResponse.length);
            this.notifyChunkCallbacks(newContent, false);
            lastPartialResponse = data.partial_response;
          }
        }

        // Handle completion
        if (data.status === 'completed') {
          console.log('Message processing completed');
          // Don't send empty reasoning - it would erase all reasoning blocks
          // The reasoning timer is stopped via handleSSEChunk when isComplete: true

          // Send final response if different from last partial
          if (data.complete_response && data.complete_response !== lastPartialResponse) {
            const newContent = data.complete_response.substring(lastPartialResponse.length);
            this.notifyChunkCallbacks(newContent, true);
          } else {
            // Just mark as complete
            this.notifyChunkCallbacks('', true);
          }
          return;
        }

        // Handle failure
        if (data.status === 'failed') {
          console.error('Message processing failed:', data.error);
          this.notifyErrorCallbacks(data.error || 'Processing failed');
          return;
        }

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        attempt++;

      } catch (error: any) {
        const statusCode = error.response?.status || error.status;

        // If message not found (404), silently stop polling - backend may have restarted
        if (statusCode === 404) {
          // Don't log error or notify user - just stop this specific poll
          return;
        }

        // Log other errors
        console.error(`Polling error (attempt ${attempt + 1}/${maxAttempts}):`, error.message);

        // Don't give up on network errors, keep trying
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, pollInterval));
          attempt++;
          continue;
        }

        this.notifyErrorCallbacks('Polling timeout or error');
        throw error;
      }
    }

    // Timeout reached
    console.error('Polling timeout after', maxAttempts * pollInterval / 1000, 'seconds');
    this.notifyErrorCallbacks('Response timeout');
  }

  public disconnectChatStream(): void {
    // No-op for polling-based system
    this.notifyConnectionCallbacks(false);
  }

  public disconnectProcessingStream(): void {
    // No-op for polling-based system
  }

  public disconnectAllStreams(): void {
    this.currentSessionId = null;
    this.isConnecting = false;
    this.notifyConnectionCallbacks(false);
  }

  // Callback management
  public onMessage(callback: MessageCallback): () => void {
    this.messageCallbacks.add(callback);
    return () => this.messageCallbacks.delete(callback);
  }

  public onChunk(callback: ChunkCallback): () => void {
    this.chunkCallbacks.add(callback);
    return () => this.chunkCallbacks.delete(callback);
  }

  public onReasoning(callback: ReasoningCallback): () => void {
    this.reasoningCallbacks.add(callback);
    return () => this.reasoningCallbacks.delete(callback);
  }

  public onError(callback: ErrorCallback): () => void {
    this.errorCallbacks.add(callback);
    return () => this.errorCallbacks.delete(callback);
  }

  public onConnection(callback: ConnectionCallback): () => void {
    this.connectionCallbacks.add(callback);
    return () => this.connectionCallbacks.delete(callback);
  }

  private notifyMessageCallbacks(message: ChatApiMessage): void {
    this.messageCallbacks.forEach(callback => {
      try {
        callback(message);
      } catch (error) {
        console.error('Error in message callback:', error);
      }
    });
  }

  private notifyChunkCallbacks(chunk: string, isComplete: boolean): void {
    this.chunkCallbacks.forEach(callback => {
      try {
        callback(chunk, isComplete);
      } catch (error) {
        console.error('Error in chunk callback:', error);
      }
    });
  }

  private notifyReasoningCallbacks(reasoning: string, isStreaming: boolean): void {
    this.reasoningCallbacks.forEach(callback => {
      try {
        callback(reasoning, isStreaming);
      } catch (error) {
        console.error('Error in reasoning callback:', error);
      }
    });
  }

  private notifyErrorCallbacks(error: string): void {
    this.errorCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (error) {
        console.error('Error in error callback:', error);
      }
    });
  }

  private notifyConnectionCallbacks(connected: boolean): void {
    this.connectionCallbacks.forEach(callback => {
      try {
        callback(connected);
      } catch (error) {
        console.error('Error in connection callback:', error);
      }
    });
  }

  public isChatStreamConnected(): boolean {
    // For polling-based system, return true if we have a session
    return this.currentSessionId !== null;
  }

  public isProcessingStreamConnected(): boolean {
    // For polling-based system, return true if we have a session
    return this.currentSessionId !== null;
  }

  public getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }
}

// Export singleton instance
export const chatAPI = ChatAPIService.getInstance();
export default chatAPI;

// Export types for use in components
export type { ChatApiMessage, User, ReasoningCallback };